import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import { FiLogOut, FiUser } from 'react-icons/fi';
import profile from './assets/profile.webp';
import {
  extractRestaurantId,
  extractRestaurantName,
  fetchUserRestaurants,
  pickDefaultRestaurantFromList,
  resolveInitialRestaurant,
} from './restaurantApi';
import { fetchActiveSupermarkets } from './groceryApi';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import { performLogout } from '../utils/sessionManager';
import { formatUserFullName } from './formatDisplay';
import {
  ENTITY_TYPE_RESTAURANT,
  ENTITY_TYPE_STORE,
  getEntityType,
  goToEntityHome,
  setEntityType,
} from './entityContext';
import { notifyRestaurantServicesContextChanged } from './useRestaurantEnabledServices';
import { notifyBusinessContextChanged, BUSINESS_CONTEXT_CHANGED } from './restaurantNav';
import PartnerBusinessSwitcher from '../partner/PartnerBusinessSwitcher';
import { ROUTES } from '../constants/appRoutes';
import '../admin/EatsAdminLayout.css';

const extractSupermarketId = (supermarket) => supermarket?.supermarketId ?? null;
const extractSupermarketName = (supermarket) => supermarket?.supermarketName ?? '';

const pickDefaultSupermarket = (supermarkets) => {
  if (!Array.isArray(supermarkets) || supermarkets.length === 0) return null;
  return supermarkets.find((s) => s.isDefault) ?? supermarkets[0];
};

const resolveHeaderEntityType = (restaurantCount, supermarketCount) => {
  const stored = getEntityType();
  if (stored === ENTITY_TYPE_STORE && supermarketCount > 0) return ENTITY_TYPE_STORE;
  if (stored === ENTITY_TYPE_RESTAURANT && restaurantCount > 0) return ENTITY_TYPE_RESTAURANT;
  if (restaurantCount > 0) return ENTITY_TYPE_RESTAURANT;
  if (supermarketCount > 0) return ENTITY_TYPE_STORE;
  return ENTITY_TYPE_RESTAURANT;
};

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

/**
 * Account menu used in the top header or the left sidebar footer.
 * @param {'header' | 'sidebar'} placement
 */
const BusinessAccountMenu = ({ placement = 'header' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(readStoredUser);
  const userId = user?.userId ?? user?.user_id ?? localStorage.getItem('userId') ?? '';

  const [restaurants, setRestaurants] = useState([]);
  const [supermarkets, setSupermarkets] = useState([]);
  const [activeRestaurantId, setActiveRestaurantId] = useState(
    () => localStorage.getItem('restaurantId') || ''
  );
  const [activeSupermarketId, setActiveSupermarketId] = useState(
    () => localStorage.getItem('storeId') || ''
  );
  const [headerEntityType, setHeaderEntityType] = useState(() => getEntityType());

  useEffect(() => {
    const refreshUser = () => setUser(readStoredUser());
    refreshUser();
    window.addEventListener('storage', refreshUser);
    window.addEventListener(BUSINESS_CONTEXT_CHANGED, refreshUser);
    return () => {
      window.removeEventListener('storage', refreshUser);
      window.removeEventListener(BUSINESS_CONTEXT_CHANGED, refreshUser);
    };
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/store/')) {
      setHeaderEntityType(ENTITY_TYPE_STORE);
      setEntityType(ENTITY_TYPE_STORE);
    }
  }, [location.pathname]);

  useEffect(() => {
    const loadRestaurants = async () => {
      if (!userId) return;
      try {
        const list = await fetchUserRestaurants(userId);
        setRestaurants(list);

        const storedId = localStorage.getItem('restaurantId');
        const storedInList =
          storedId && list.some((r) => String(extractRestaurantId(r)) === String(storedId));

        if (storedInList) {
          setActiveRestaurantId(String(storedId));
          return;
        }

        const initial = await resolveInitialRestaurant(userId, list);
        if (initial) {
          const id = String(extractRestaurantId(initial));
          const name = extractRestaurantName(initial);
          localStorage.setItem('restaurantId', id);
          if (name) localStorage.setItem('restaurantName', name);
          notifyRestaurantServicesContextChanged();
          notifyBusinessContextChanged();
          setActiveRestaurantId(id);
          return;
        }

        const fallback = pickDefaultRestaurantFromList(list);
        if (fallback) {
          const id = String(extractRestaurantId(fallback));
          const name = extractRestaurantName(fallback);
          localStorage.setItem('restaurantId', id);
          if (name) localStorage.setItem('restaurantName', name);
          notifyRestaurantServicesContextChanged();
          notifyBusinessContextChanged();
          setActiveRestaurantId(id);
        }
      } catch (error) {
        console.error('Error fetching restaurants:', error);
      }
    };

    const loadSupermarkets = async () => {
      if (!userId) return;
      try {
        const list = await fetchActiveSupermarkets(userId);
        setSupermarkets(list);

        const storedId = localStorage.getItem('storeId');
        const storedInList =
          storedId && list.some((s) => String(extractSupermarketId(s)) === String(storedId));

        if (storedInList) {
          setActiveSupermarketId(String(storedId));
          return;
        }

        const fallback = pickDefaultSupermarket(list);
        if (fallback) {
          const id = String(extractSupermarketId(fallback));
          const name = extractSupermarketName(fallback);
          localStorage.setItem('storeId', id);
          if (name) localStorage.setItem('storeName', name);
          notifyBusinessContextChanged();
          setActiveSupermarketId(id);
        }
      } catch (error) {
        console.error('Error fetching supermarkets:', error);
      }
    };

    loadRestaurants();
    loadSupermarkets();
  }, [userId]);

  useEffect(() => {
    const nextType = resolveHeaderEntityType(restaurants.length, supermarkets.length);
    setHeaderEntityType((current) => {
      const stored = getEntityType();
      if (stored === ENTITY_TYPE_STORE && supermarkets.length > 0) return ENTITY_TYPE_STORE;
      if (stored === ENTITY_TYPE_RESTAURANT && restaurants.length > 0) {
        return ENTITY_TYPE_RESTAURANT;
      }
      if (current === ENTITY_TYPE_STORE && supermarkets.length > 0) return ENTITY_TYPE_STORE;
      if (current === ENTITY_TYPE_RESTAURANT && restaurants.length > 0) {
        return ENTITY_TYPE_RESTAURANT;
      }
      return nextType;
    });
  }, [restaurants.length, supermarkets.length]);

  const handleSignOut = () => {
    performLogout({ redirectPath: '/partner/login' });
  };

  const handleRestaurantSwitch = (restaurant) => {
    const id = String(extractRestaurantId(restaurant));
    const name = extractRestaurantName(restaurant);
    const sameRestaurant = id === String(activeRestaurantId);
    const inRestaurantMode = headerEntityType === ENTITY_TYPE_RESTAURANT;
    if (sameRestaurant && inRestaurantMode) return;

    localStorage.setItem('restaurantId', id);
    if (name) localStorage.setItem('restaurantName', name);
    setEntityType(ENTITY_TYPE_RESTAURANT);
    notifyBusinessContextChanged();

    try {
      const updatedUser = { ...user, restaurantId: Number(id) || id };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch {
      /* ignore */
    }

    goToEntityHome(ENTITY_TYPE_RESTAURANT);
  };

  const handleSupermarketSwitch = (supermarket) => {
    const id = String(extractSupermarketId(supermarket));
    const name = extractSupermarketName(supermarket);
    const sameStore = id === String(activeSupermarketId);
    const inStoreMode = headerEntityType === ENTITY_TYPE_STORE;
    if (sameStore && inStoreMode) return;

    localStorage.setItem('storeId', id);
    if (name) localStorage.setItem('storeName', name);
    setEntityType(ENTITY_TYPE_STORE);
    notifyBusinessContextChanged();

    try {
      const updatedUser = { ...user, storeId: Number(id) || id };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch {
      /* ignore */
    }

    goToEntityHome(ENTITY_TYPE_STORE);
  };

  const openRestaurantDetails = (restaurant, detailTab = 'details', { readOnly = false } = {}) => {
    const id = String(extractRestaurantId(restaurant) || activeRestaurantId || '');
    if (!id) return;
    const params = new URLSearchParams();
    if (detailTab) params.set('tab', detailTab);
    if (readOnly) params.set('view', 'readonly');
    const qs = params.toString();
    navigate(`${RESTAURANT_API_PATHS.restaurantById(id)}${qs ? `?${qs}` : ''}`);
  };

  const openSupermarketDetails = (supermarket) => {
    const id = String(extractSupermarketId(supermarket));
    if (!id) return;
    const isEatsApp = String(user?.domain || '').toUpperCase() === 'EATS_APP';
    if (isEatsApp) {
      navigate(`/eats/admin/stores/${id}`);
      return;
    }
    navigate(`/store/${id}`);
  };

  const activeRestaurant = restaurants.find(
    (r) => String(extractRestaurantId(r)) === String(activeRestaurantId)
  );
  const activeSupermarket = supermarkets.find(
    (s) => String(extractSupermarketId(s)) === String(activeSupermarketId)
  );
  const isEatsAppUser = String(user?.domain || '').toUpperCase() === 'EATS_APP';
  const activeRestaurantName =
    extractRestaurantName(activeRestaurant) || localStorage.getItem('restaurantName') || '';
  const activeSupermarketName =
    extractSupermarketName(activeSupermarket) || localStorage.getItem('storeName') || '';
  const showSupermarketHeader =
    headerEntityType === ENTITY_TYPE_STORE &&
    Boolean(activeSupermarketId || activeSupermarketName);
  const headerDisplayName = showSupermarketHeader ? activeSupermarketName : activeRestaurantName;
  const displayName = formatUserFullName(user);
  const displayEmail =
    user?.email || user?.userName || headerDisplayName || (showSupermarketHeader ? 'Store' : 'Restaurant');

  const panel = (
    <div className="maya-account-menu__panel" role="menu">
      <div className="maya-account-menu__summary">
        <img src={profile} alt="" className="maya-account-menu__summary-avatar" />
        <div className="maya-account-menu__summary-text">
          <strong>{displayName}</strong>
          <span>{displayEmail}</span>
        </div>
      </div>
      <div className="maya-account-menu__divider" />

      {restaurants.length > 1 && (
        <>
          <span className="maya-account-menu__section-label">Switch restaurant</span>
          <div className="maya-account-menu__entity-list">
            {restaurants.map((restaurant) => {
              const id = String(extractRestaurantId(restaurant));
              const name = extractRestaurantName(restaurant) || 'Restaurant';
              const isActive =
                headerEntityType === ENTITY_TYPE_RESTAURANT && id === String(activeRestaurantId);

              return (
                <div key={id} className="maya-account-menu__entity-row">
                  <button
                    type="button"
                    className={`maya-account-menu__item${isActive ? ' is-active' : ''}`}
                    onClick={() => handleRestaurantSwitch(restaurant)}
                  >
                    <span>{name}</span>
                  </button>
                  <button
                    type="button"
                    className="maya-account-menu__entity-details"
                    onClick={() => openRestaurantDetails(restaurant)}
                    aria-label={`View details for ${name}`}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="maya-account-menu__divider" />
        </>
      )}

      {supermarkets.length > 1 && (
        <>
          <span className="maya-account-menu__section-label">Switch supermarket</span>
          <div className="maya-account-menu__entity-list">
            {supermarkets.map((supermarket) => {
              const id = String(extractSupermarketId(supermarket));
              const name = extractSupermarketName(supermarket) || 'Supermarket';
              const isActive =
                headerEntityType === ENTITY_TYPE_STORE && id === String(activeSupermarketId);

              return (
                <div key={id} className="maya-account-menu__entity-row">
                  <button
                    type="button"
                    className={`maya-account-menu__item${isActive ? ' is-active' : ''}`}
                    onClick={() => handleSupermarketSwitch(supermarket)}
                  >
                    <span>{name}</span>
                  </button>
                  <button
                    type="button"
                    className="maya-account-menu__entity-details"
                    onClick={() => openSupermarketDetails(supermarket)}
                    aria-label={`View details for ${name}`}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="maya-account-menu__divider" />
        </>
      )}

      <Link
        to={isEatsAppUser ? ROUTES.userProfile : ROUTES.businessProfile}
        className="maya-account-menu__item"
        role="menuitem"
      >
        <FiUser aria-hidden="true" />
        <span>Profile</span>
      </Link>
      <PartnerBusinessSwitcher />
      <div className="maya-account-menu__divider" />
      <button
        type="button"
        className="maya-account-menu__item maya-account-menu__item--danger"
        role="menuitem"
        onClick={handleSignOut}
      >
        <FiLogOut aria-hidden="true" />
        <span>Sign out</span>
      </button>
    </div>
  );

  if (placement === 'sidebar') {
    const profilePath = isEatsAppUser ? ROUTES.userProfile : ROUTES.businessProfile;
    return (
      <Link
        to={profilePath}
        className="maya-account-menu maya-account-menu--sidebar maya-account-menu__sidebar-trigger"
        title={displayName}
      >
        <img src={profile} alt="" className="maya-account-menu__avatar" />
        <span className="maya-account-menu__sidebar-meta">
          <span className="maya-account-menu__name">{displayName}</span>
          <span className="maya-account-menu__email">{displayEmail}</span>
        </span>
      </Link>
    );
  }

  return (
    <div className="maya-account-menu">
      <button type="button" className="maya-account-menu__trigger" aria-haspopup="menu">
        <img src={profile} alt="" className="maya-account-menu__avatar" />
        <span className="maya-account-menu__name">{displayName}</span>
      </button>
      {panel}
    </div>
  );
};

export default BusinessAccountMenu;
